import React, { useMemo } from "react";
import {
    ACCOUNTING_REPORT_LABELS,
    AccountingReportType,
} from "@common/types/reports/report-type";
import { useLedgers } from "@api/ledger.api";
import { useEntities } from "@hooks/useEntities";
import { useScope } from "@state/scopeContext.lib";
import {
    buildConsolidatedReportUrl,
    canOpenConsolidatedReport,
    resolveConsolidatedReportLedgerId,
} from "@components/ownership/ownershipConsolidationReports.helpers";
import { buildEliminationDetailUrl } from "./buildEliminationDetailUrl";
import { buildEliminationExceptionsUrl } from "./buildEliminationExceptionsUrl";
import { useReportConsolidationGroup } from "./useReportConsolidationGroup";

export interface AccountingReportItem {
    id: string;
    title: React.ReactNode;
    description: string;
    link?: string;
    active?: boolean;
    onClick?: () => void;
    loading?: boolean;
}

/**
 * The consolidated variant of the accountant report list, narrowed to the
 * reports that support consolidation-group scoping. Returns null when the
 * requested group is not the workspace's active consolidation group, in which
 * case the caller keeps the per-entity report list.
 *
 * The P&L / Balance Sheet links are scoped to the group's parent entity and the
 * ledger resolved for the group basis — not to the current entity filter — so
 * they match the links built on the ownership canvas and in the sidebar.
 */
export function useConsolidatedAccountingReports({
    consolidationGroupId,
    startDate,
    endDate,
}: {
    consolidationGroupId: string | undefined;
    startDate: string;
    endDate: string;
}): AccountingReportItem[] | null {
    const { workspaceId } = useScope();
    const { consolidationGroup } = useReportConsolidationGroup();
    const group =
        consolidationGroupId && consolidationGroup?.id === consolidationGroupId
            ? consolidationGroup
            : undefined;

    const entities = useEntities();
    const parentEntity = entities.find(
        ({ uuid }) => uuid === group?.parentEntityUuid,
    );
    const { data: ledgers } = useLedgers(parentEntity?.id);
    const ledgerId = group
        ? resolveConsolidatedReportLedgerId({ group, ledgers })
        : undefined;

    return useMemo(() => {
        if (!group) {
            return null;
        }
        const eliminationUrlParams = {
            startDate,
            endDate,
            workspaceId: workspaceId ?? undefined,
            consolidationGroupId: group.id,
        };
        const consolidatedReports: AccountingReportItem[] = [];
        if (
            parentEntity &&
            ledgerId &&
            canOpenConsolidatedReport({ group, parentEntity, ledgerId })
        ) {
            const reportUrlParams = {
                parentEntity,
                ledgerId,
                group,
                dateRange: { startDate, endDate },
            };
            consolidatedReports.push(
                {
                    id: "profit-loss",
                    title: (
                        <>
                            {
                                ACCOUNTING_REPORT_LABELS[
                                    AccountingReportType.PROFIT_LOSS
                                ]
                            }{" "}
                            <small>(Consolidated)</small>
                        </>
                    ),
                    description:
                        "Consolidated income and expenses across the group's entities over a specified period of time",
                    link: buildConsolidatedReportUrl({
                        report: "profit-loss",
                        ...reportUrlParams,
                    }),
                    active: true,
                },
                {
                    id: "balance-sheet",
                    title: (
                        <>
                            {
                                ACCOUNTING_REPORT_LABELS[
                                    AccountingReportType.BALANCE_SHEET
                                ]
                            }{" "}
                            <small>(Consolidated)</small>
                        </>
                    ),
                    description:
                        "Consolidated snapshot of the group's financial position, broken down into assets, liabilities, and equity",
                    link: buildConsolidatedReportUrl({
                        report: "balance-sheet",
                        ...reportUrlParams,
                    }),
                    active: true,
                },
            );
        }
        consolidatedReports.push(
            {
                id: "elimination-detail",
                title: ACCOUNTING_REPORT_LABELS[
                    AccountingReportType.ELIMINATION_DETAIL
                ],
                description:
                    "Ledger of intercompany eliminating entries recorded for the consolidation group",
                link: buildEliminationDetailUrl(eliminationUrlParams),
                active: true,
            },
            {
                id: "elimination-exceptions",
                title: ACCOUNTING_REPORT_LABELS[
                    AccountingReportType.ELIMINATION_EXCEPTIONS
                ],
                description:
                    "Intercompany activity that could not be eliminated automatically and needs review",
                link: buildEliminationExceptionsUrl(eliminationUrlParams),
                active: true,
            },
        );
        return consolidatedReports;
    }, [group, parentEntity, ledgerId, startDate, endDate, workspaceId]);
}
